// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calendar,
  Clock,
  Plus,
  TrendingUp,
  CheckCircle,
  Star,
  Users,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Filter,
  MoreHorizontal,
  Trophy,
  Target,
  Smile
} from "lucide-react";
import { useOneOnOneSessions, useActionItems } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";

export function OneOnOnesPage() {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionsPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState('upcoming');

  const { data: sessionsData, isLoading: sessionsLoading } = useOneOnOneSessions({
    page: currentPage,
    limit: sessionsPerPage
  });

  const { data: actionItemsData, isLoading: actionItemsLoading } = useActionItems({
    status: 'pending'
  });

  const sessions = sessionsData?.data || [];
  const actionItems = actionItemsData?.data || [];

  const sessionAchievements = useMemo(() => {
    return sessions.flatMap((session: any) => {
      if (!Array.isArray(session?.achievements)) {
        return []
      }

      return session.achievements.map((achievement: any, index: number) => ({
        id: achievement?.id || `${session.id}:achievement:${index}`,
        text: achievement?.achievement_text ?? achievement?.text ?? 'Achievement unlocked',
        createdByRole: achievement?.created_by_role ?? achievement?.createdByRole ?? 'manager',
        createdBy: achievement?.created_by ?? achievement?.createdBy ?? null,
        sessionId: session.id,
        sessionTitle: session.title ?? '1-on-1 Session',
        completedAt: session.completedAt ?? session.updatedAt ?? session.createdAt,
      }))
    })
  }, [sessions])

  const parseDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  // Separate upcoming and past sessions
  const now = new Date();
  const upcomingSessions = sessions.filter((session) => {
    const scheduledDate = parseDate(session.scheduledDate as string | null);
    return scheduledDate ? scheduledDate > now : false;
  });

  const pastSessions = sessions.filter((session) => {
    const scheduledDate = parseDate(session.scheduledDate as string | null);
    if (!scheduledDate) {
      return false;
    }
    return scheduledDate <= now && session.status === 'completed';
  });

  // Calculate metrics
  const totalSessions = sessions.length;
  const completedSessions = pastSessions.length;
  const pendingActionItems = actionItems.filter(item => item.status === 'pending').length;
  const averageMoodRating = pastSessions
    .filter(s => s.mood)
    .reduce((sum, s) => sum + s.mood!, 0) / pastSessions.filter(s => s.mood).length || 0;
  const averageProductivityRating = pastSessions
    .filter(s => s.productivityRating)
    .reduce((sum, s) => sum + s.productivityRating!, 0) / pastSessions.filter(s => s.productivityRating).length || 0;

  // Mock achievements data
  const mockAchievements = [
    {
      id: '1',
      title: 'Consistent Communicator',
      description: 'Attended 10 consecutive 1-on-1 sessions',
      icon: Trophy,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      progress: 100,
      unlocked: true
    },
    {
      id: '2',
      title: 'Goal Crusher',
      description: 'Completed 25 action items',
      icon: Target,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      progress: 84,
      unlocked: false
    },
    {
      id: '3',
      title: 'Feedback Champion',
      description: 'Provided feedback in 15 sessions',
      icon: MessageSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      progress: 67,
      unlocked: false
    }
  ];

  const achievements = sessionAchievements.length > 0 ? sessionAchievements : mockAchievements;

  // Pagination for upcoming sessions
  const totalUpcomingPages = Math.ceil(upcomingSessions.length / sessionsPerPage);
  const upcomingSessionsToShow = upcomingSessions.slice(
    (currentPage - 1) * sessionsPerPage,
    currentPage * sessionsPerPage
  );

  const formatDate = (dateString?: string | null) => {
    const date = parseDate(dateString);
    if (!date) {
      return 'To be scheduled';
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDueDate = (value?: string | null) => {
    const date = parseDate(value);
    if (!date) {
      return 'No due date';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (value?: number | null) => {
    if (typeof value === 'number' && value > 0) {
      return value;
    }
    return 30;
  };

  const getMoodEmoji = (mood: number) => {
    if (mood >= 4.5) return '😊';
    if (mood >= 3.5) return '🙂';
    if (mood >= 2.5) return '😐';
    if (mood >= 1.5) return '😕';
    return '😞';
  };

  if (sessionsLoading || actionItemsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading your 1-on-1 sessions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/20 via-background to-muted/30 p-6 dark:from-background dark:via-background dark:to-background">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">My 1-on-1 Sessions</h1>
            <p className="text-sm text-muted-foreground">
              Track your personal 1-on-1 sessions, metrics, and development progress
            </p>
          </div>
          <Link to="/one-on-ones/schedule">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Schedule 1-on-1
            </Button>
          </Link>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition-colors">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/15">
              <Users className="h-6 w-6" />
            </div>
            <div className="mb-1 text-3xl font-bold text-foreground">{totalSessions}</div>
            <div className="text-sm text-muted-foreground">Total Sessions</div>
            <div className="mt-1 text-xs text-muted-foreground">0 physical • 0 email</div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition-colors">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/15">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div className="mb-1 text-3xl font-bold text-foreground">{pendingActionItems}</div>
            <div className="text-sm text-muted-foreground">Action Items</div>
            <div className="mt-1 text-xs text-muted-foreground">0 completed (0%)</div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition-colors">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 dark:bg-emerald-400/15 dark:text-emerald-300">
              <Smile className="h-6 w-6" />
            </div>
            <div className="mb-1 text-3xl font-bold text-foreground">
              {averageMoodRating > 0 ? `${averageMoodRating.toFixed(1)}/5` : '0.0/5'}
            </div>
            <div className="text-sm text-muted-foreground">Mood Rating</div>
            <div className="mt-1 text-xs text-muted-foreground">Average mood across sessions</div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition-colors">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10 text-orange-500 dark:bg-orange-400/15 dark:text-orange-300">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div className="mb-1 text-3xl font-bold text-foreground">
              {averageProductivityRating > 0 ? `${averageProductivityRating.toFixed(1)}/5` : '0.0/5'}
            </div>
            <div className="text-sm text-muted-foreground">Productivity</div>
            <div className="mt-1 text-xs text-muted-foreground">Average productivity rating</div>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="rounded-2xl border border-border bg-card p-6 mb-8 shadow-sm transition-colors">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-500 dark:bg-amber-500/20 dark:text-amber-300">
              <Trophy className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Your Achievements</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Celebrating your accomplishments and progress from 1-on-1 sessions
          </p>

          {achievements.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-500 dark:bg-amber-500/20 dark:text-amber-300">
                <Trophy className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Achievements Yet</h3>
              <p className="text-sm text-muted-foreground">
                Your achievements and accomplishments from 1-on-1 sessions will be displayed here.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {achievements.map((achievement) => {
                const completedAt = achievement.completedAt ? new Date(achievement.completedAt) : null

                return (
                  <div key={achievement.id} className="rounded-xl border border-border/60 bg-muted/20 p-4 dark:border-border dark:bg-muted/40">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{achievement.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Recognized in <Link className="underline decoration-dotted" to={`/one-on-ones/${achievement.sessionId}`}>{achievement.sessionTitle}</Link>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 capitalize">
                          Added by {achievement.createdByRole?.toLowerCase() ?? 'manager'}
                        </p>
                      </div>
                      {completedAt ? (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {completedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Overall Happiness */}
        <div className="rounded-2xl border border-border bg-card p-6 mb-8 shadow-sm transition-colors">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/15">
              <Smile className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Overall Happiness: {averageMoodRating > 0 ? averageMoodRating.toFixed(1) : '0.0'}/5</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Your average happiness rating across all completed 1-on-1 sessions
          </p>
        </div>

        {/* Sessions Tabs */}
        <div className="mb-8">
          <div className="flex border-b border-border mb-6">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'upcoming'
                  ? 'text-foreground border-b-2 border-blue-600'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Upcoming Sessions
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'history'
                  ? 'text-foreground border-b-2 border-blue-600'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Session History
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'upcoming' ? (
            /* My Upcoming Sessions */
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-colors">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/15">
                  <Calendar className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">My Upcoming Sessions</h2>
              </div>

              {upcomingSessions.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/40 text-muted-foreground dark:bg-muted/30">
                    <Calendar className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Upcoming Sessions</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Your manager will schedule 1-on-1 sessions with you from your team profile. Check back later or reach out to your manager.
                  </p>
                  <Link to="/one-on-ones/schedule">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Plus className="h-4 w-4 mr-2" />
                      Request Session
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <div key={session.id} className="rounded-lg border border-border bg-card/40 p-4 transition-colors hover:bg-muted/40 dark:bg-card dark:hover:bg-muted/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground">{session.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(session.scheduledDate as string | null)} • {formatDuration(session.duration)} minutes
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Clock className="h-4 w-4 mr-1" />
                            Reschedule
                          </Button>
                          <Button size="sm">Join</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Session History */
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-colors">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 dark:bg-emerald-400/15 dark:text-emerald-300">
                  <CheckCircle className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Session History</h2>
              </div>

              {pastSessions.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/40 text-muted-foreground dark:bg-muted/30">
                    <Clock className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Session History</h3>
                  <p className="text-sm text-muted-foreground">
                    Your completed sessions will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pastSessions.map((session) => (
                    <div key={session.id} className="rounded-lg border border-border bg-card/40 p-4 transition-colors hover:bg-muted/40 dark:bg-card dark:hover:bg-muted/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground">{session.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(session.scheduledDate as string | null)} • {formatDuration(session.duration)} minutes
                          </p>
                          <div className="flex gap-2 mt-2">
                            {session.mood && (
                              <Badge variant="outline" className="text-xs">
                                Mood: {getMoodEmoji(session.mood)} {session.mood.toFixed(1)}
                              </Badge>
                            )}
                            {session.productivityRating && (
                              <Badge variant="outline" className="text-xs">
                                Productivity: {session.productivityRating.toFixed(1)}/5
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending Action Items */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-colors">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/10 text-orange-500 dark:bg-orange-400/15 dark:text-orange-300">
              <Target className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Pending Action Items</h2>
              <p className="text-sm text-muted-foreground">
                Items that need your attention from recent 1-on-1 sessions
              </p>
            </div>
          </div>

          {actionItems.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/40 text-muted-foreground dark:bg-muted/30">
                <Target className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Action Items</h3>
              <p className="text-sm text-muted-foreground">
                Action items from your 1-on-1 sessions will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {actionItems.slice(0, 5).map((item) => (
                <div key={item.id} className="rounded-lg border border-border bg-card/40 p-4 transition-colors hover:bg-muted/40 dark:bg-card dark:hover:bg-muted/20">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2" />
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                        )}
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            Due: {formatDueDate(item.dueDate as string | null)}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">
                            {item.priority}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Mark Complete
                    </Button>
                  </div>
                </div>
              ))}
              {actionItems.length > 5 && (
                <div className="text-center pt-4">
                  <Link to="/one-on-ones/action-items">
                    <Button variant="outline">
                      View All Action Items ({actionItems.length})
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
